import React, { useEffect, useRef, useState } from 'react';
import 'intersection-observer';

import { Box, Page } from '@zillow/constellation-10';

import type { ReactNode } from 'react';

type Props = {
  options?: {
    root: Element | null;
    rootMargin: string;
    threshold: Array<number>;
  };
  useWhiteBackground?: boolean;
  addBorderBottom?: boolean;
  children: ReactNode;
  passDownFixedStatus?: boolean;
  offsetForLeftNav?: boolean;
  fixedOuterContainerStyles?: React.CSSProperties | null;
  isLoading?: boolean;
};

const StickyContainer = ({
  // These are the actual defaults for the IntersectionObserver API
  // including them here for clarity
  options = {
    root: null, // null defaults to the viewport
    rootMargin: '0px 0px 0px 0px', // offsets for one or more sides of the root's bounding box
    threshold: [0.0], // can use single number or a range between 0.0-1.0
  },
  useWhiteBackground = true,
  addBorderBottom,
  children,
  passDownFixedStatus = false,
  offsetForLeftNav = false,
  fixedOuterContainerStyles = null,
  isLoading = false,
}: Props) => {
  const [isFixedPosition, setIsFixedPosition] = useState(false);
  const scrollRef = useRef(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Don't set fixed position if we're in loading state
    if (isLoading) {
      setIsFixedPosition(false);
      return;
    }

    observer.current = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
        setIsFixedPosition(true);
      } else {
        setIsFixedPosition(false);
      }
    }, options);

    if (scrollRef.current) {
      observer.current.observe(scrollRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [isLoading]);

  const renderChildren = () => {
    return passDownFixedStatus
      ? React.Children.map(children, (child) => {
          return React.cloneElement(child as React.ReactElement<{ isFixedPosition: boolean }>, { isFixedPosition });
        })
      : children;
  };

  const renderContent = () => {
    return (
      <Page.Root
        fluid={true}
        css={{
          position: isFixedPosition ? 'fixed' : 'sticky',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          backgroundColor: isFixedPosition === true && useWhiteBackground ? 'white' : 'inherit',
          borderBottom: isFixedPosition === true && addBorderBottom ? '1px solid {colors.border.subtle}' : undefined,
          zIndex: isFixedPosition ? '2000' : '0',
          xl: {
            left: offsetForLeftNav ? '{sizes.leftNavWidth}' : 0,
            margin: '0 auto',
          },
          paddingBlockEnd: '0',
          _before: { content: '" "', display: 'table' },
        }}
      >
        <Page.Content
          data-testid="StickyContainer-PageContent"
          fluid={false}
          css={{ marginBlock: '0', paddingInline: '0' }}
        >
          {renderChildren()}
        </Page.Content>
      </Page.Root>
    );
  };

  return (
    <div data-testid="StickyContainer">
      <Box ref={scrollRef} css={{ height: 0, visibility: 'hidden' }} />
      {fixedOuterContainerStyles !== null ? (
        <Page.Root
          css={{
            backgroundColor: isFixedPosition === true && useWhiteBackground ? 'white' : 'inherit',
            width: '100%',
            borderBottom: isFixedPosition === true && addBorderBottom ? '1px solid {colors.border.subtle}' : undefined,
            top: 0,
            position: isFixedPosition ? 'fixed' : 'sticky',
            zIndex: isFixedPosition ? '2000' : '0',
            xl: {
              left: offsetForLeftNav ? '{sizes.leftNavWidth}' : 0,
            },
            paddingBlockEnd: '0',
            _before: { content: '" "', display: 'table' },
          }}
          style={isFixedPosition === true ? fixedOuterContainerStyles : undefined}
        >
          {renderContent()}
        </Page.Root>
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default StickyContainer;
